import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import { ProfileResponse, UpdateProfilePayload } from "@/types/profile.types";
import {
  CreateUserPayload,
  UserListQueryParams,
  UserResponse,
} from "@/types/user-management.types";

export const UserManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all users with filters
    getUsers: builder.query<
      PaginatedApiResponse<UserResponse>,
      UserListQueryParams
    >({
      query: (params) => {
        return {
          url: `user-management`,
          method: "GET",
          params: {
            page: params?.page,
            limit: params?.limit,
            user_type: params?.user_type,
            roles: params?.roles,
            status: params?.status,
            search: params?.search,
            start_date: params?.start_date,
            end_date: params?.end_date,
            entity: params?.entity,
            entityId: params?.entityId,
            club_id: params?.club_id,
          },
        };
      },
      providesTags: [{ type: "UserManagement", id: "LIST" }],
      keepUnusedDataFor: 0,
    }),

    // Create a new user
    createUser: builder.mutation<
      ApiResponse<UserResponse>,
      CreateUserPayload | FormData
    >({
      query: (user) => ({
        url: "user-management",
        method: "POST",
        data: user,
        headers: {
          "x-lang": "en",
        },
      }),
      transformResponse: (response: ApiResponse<UserResponse>) => response,
      invalidatesTags: [{ type: "UserManagement", id: "LIST" }],
    }),

    // Get user details by ID
    getUserById: builder.query<UserResponse, string>({
      query: (user_id) => ({
        url: `/user-management/${user_id}`,
        method: "GET",
        headers: {
          "x-lang": "en",
        },
      }),
      transformResponse: (response: UserResponse) => response,
      providesTags: (result, error, user_id) => [
        { type: "UserManagement", id: user_id },
      ],
      keepUnusedDataFor: 0,
    }),

    // Update user by ID
    updateUser: builder.mutation<
      ApiResponse<ProfileResponse>,
      { user_id: string; data: UpdateProfilePayload | FormData }
    >({
      query: ({ user_id, data }) => ({
        url: `/user-management/${user_id}`,
        method: "PATCH",
        data,
        headers: {
          "x-lang": "en",
        },
      }),
      transformResponse: (response: ApiResponse<ProfileResponse>) => response,
      invalidatesTags: (result, error, { user_id }) => [
        { type: "UserManagement", id: user_id },
        { type: "UserManagement", id: "LIST" },
      ],
    }),

    // Delete user by ID
    deleteUser: builder.mutation<ApiResponse<null>, string>({
      query: (user_id) => ({
        url: `/user-management/${user_id}`,
        method: "DELETE",
        headers: {
          "x-lang": "en",
        },
      }),
      transformResponse: (response: ApiResponse<null>) => response,
      invalidatesTags: [{ type: "UserManagement", id: "LIST" }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useCreateUserMutation,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = UserManagementService;
